import { z } from "zod";

/**
 * The Practice Interview prep brief: what the candidate reads before the call, and what the
 * interviewer knows about the company. Every company fact carries the page it came from;
 * facts whose page was not among the searched sources are dropped on the server.
 */

export const PRACTICE_COMPANY_FACT_KINDS = ["what_they_do", "news", "values", "culture"] as const;

export const practiceCompanyFactSchema = z.object({
  kind: z.enum(PRACTICE_COMPANY_FACT_KINDS),
  text: z.string().max(400),
  url: z.string().url(),
  /** Hostname shown on the citation chip, e.g. "canva.com". */
  source: z.string().max(120),
});
export type PracticeCompanyFact = z.infer<typeof practiceCompanyFactSchema>;

export const practiceCompanyBriefSchema = z.object({
  company: z.string().max(120),
  /** The company's own site, e.g. "canva.com": research is limited to it. */
  website: z.string().max(253).optional(),
  summary: z.string().max(600),
  facts: z.array(practiceCompanyFactSchema).max(6),
  researchedAt: z.string(),
});
export type PracticeCompanyBrief = z.infer<typeof practiceCompanyBriefSchema>;

export const practicePrepBriefSchema = z.object({
  version: z.literal(1),
  company: practiceCompanyBriefSchema.nullable(),
  /** Lines from the job description, and what the interviewer is likely to ask about them. */
  probes: z.array(z.object({ jdQuote: z.string().max(300), expect: z.string().max(300) })).max(4),
  /** Resume lines worth telling as stories, and the requirement each one fits. */
  stories: z
    .array(z.object({ resumeLine: z.string().max(300), requirement: z.string().max(200) }))
    .max(3),
  /** One gap between the job and the resume to have an answer ready for. */
  watchOut: z.string().max(400).nullable(),
  generatedAt: z.string(),
});
export type PracticePrepBrief = z.infer<typeof practicePrepBriefSchema>;

/** A compact, citation-free version for the interviewer's instructions. */
export const practiceCompanyContext = (brief: PracticeCompanyBrief | null): string =>
  brief
    ? [brief.summary, ...brief.facts.map((fact) => `- ${fact.text}`)].join("\n").slice(0, 1200)
    : "";

/**
 * Sites whose pages are about other companies: job boards, social networks, directories and our
 * own job board. Their home page is fine (LinkedIn is an employer too), but a page on them, such
 * as linkedin.com/company/canva or seek.com.au/job/123, is not the employer's own website.
 */
const LISTING_SITES = [
  "linkedin.com",
  "indeed.com",
  "seek.com.au",
  "seek.co.nz",
  "glassdoor.com",
  "glassdoor.com.au",
  "glassdoor.co.in",
  "naukri.com",
  "monster.com",
  "ziprecruiter.com",
  "facebook.com",
  "instagram.com",
  "x.com",
  "twitter.com",
  "wikipedia.org",
  "crunchbase.com",
  "theresume.io",
  "firstround.com.au",
  "tryapex25.com",
];
/** Applicant tracking systems: a subdomain (boards.greenhouse.io/acme) is a company's job board. */
const ATS_SITES = [
  "greenhouse.io",
  "lever.co",
  "myworkdayjobs.com",
  "workable.com",
  "ashbyhq.com",
  "smartrecruiters.com",
];

export type PracticeCompanySite =
  | { domain: string }
  | { problem: "invalid" | "not_company_site"; domain?: string };

/**
 * Reads the company website the candidate typed ("https://www.Canva.com/about" → "canva.com").
 * Null when nothing was typed.
 */
export const practiceCompanySite = (
  input: string | null | undefined,
): PracticeCompanySite | null => {
  const raw = (input ?? "").trim().toLowerCase();
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//.test(raw) ? raw : `https://${raw}`);
  } catch {
    return { problem: "invalid" };
  }
  const host = url.hostname.replace(/^www\./, "");
  const path = url.pathname.replace(/\/+$/, "");
  const valid = /^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(host);
  if (!valid) return { problem: "invalid" };
  const on = (site: string) => host === site || host.endsWith(`.${site}`);
  const listingPage = path !== "" && LISTING_SITES.some(on);
  const atsBoard = ATS_SITES.some((site) => host.endsWith(`.${site}`));
  if (listingPage || atsBoard) return { problem: "not_company_site", domain: host };
  return { domain: host };
};

/** The domain to research, or null when the input isn't a usable company website. */
export const practiceCompanyDomain = (input: string | null | undefined): string | null => {
  const site = practiceCompanySite(input);
  return site && !("problem" in site) ? site.domain : null;
};
